import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersistence from 'vuex-persist'
import { vuexLogRocketPlugin } from '@/plugins/VuexLogrocket'
import * as actions from './actions'
import activities from './modules/activities'
import attachments from './modules/attachments'
import businessUnits from './modules/business-units'
import companies from './modules/companies'
import costCenters from './modules/cost-centers'
import dataErrors from './modules/data-errors'
import dayView, { SET_SHOW_JOBS_TABLE } from './modules/day-view'
import divisions from './modules/divisions'
import featureFlags from './modules/feature-flags'
import { dateWindowFlag, mapMaxDateRangeDays } from '@/components/field-view-next/date-window'
import firebase from './modules/firebase'
import formSubmissions, { SET_MEMORY_FIELDS } from './modules/form-submissions'
import formSubmissionsFilters, {
  RESET_INVERSE_FORM_ACTIONS,
  RESET_INVERSE_FORM_IDS,
  RESET_INVERSE_LOCATIONS,
  RESET_SUBJECT_SEARCH_IDS,
  RESET_TAGS,
  SET_ACTIVE_SAVED_FILTER_ID,
  SET_ASIDE_VISIBILITY,
  SET_DATE_RANGE,
  SET_INVERSE_FORM_ACTIONS,
  SET_INVERSE_FORM_IDS,
  SET_INVERSE_LOCATIONS,
  SET_SUBJECT_SEARCH_BUTTONS,
  SET_SUBJECT_SEARCH_IDS,
  SET_TAGS,
} from './modules/form-submissions-filters'
import forms from './modules/forms'
import inventory from './modules/inventory'
import jobs from './modules/jobs'
import jobsList, {
  JOBS_LIST_INITIALIZED,
  SET_COLUMN_ORDERING,
  SET_VISIBLE_COLUMNS,
} from './modules/jobs-list'
import notifications, {
  SET_NOTIFICATIONS_BINDING,
  SET_NOTIFICATIONS_TOKEN,
} from './modules/notifications'
import organizationConfig from './modules/organization-config'
import permissions from './modules/permissions'
import projects from './modules/projects'
import resourceTypes from './modules/resource-types'
import schemas from './modules/schemas'
import services from './modules/services'
import tasks from './modules/tasks'
import tickets from './modules/tickets'
import unitNumbers from './modules/unit-numbers'
import users from './modules/users'
import weekView, { SET_SELECTED_RESOURCE_TYPE_ID } from './modules/week-view'
import mutations from './mutations'
import state from './state'

Vue.use(Vuex)

/**
 * @todo (vue3): Uses `process.env.*` instead of `import.meta.env` only because
 *       Jest cannot parse import.meta. Switch when tests move to Vitest; the
 *       `define` block in vite.config.mts is what keeps this working.
 */
const debug = process.env.NODE_ENV !== 'production'

const vuexLocal = new VuexPersistence({
  storage: globalThis.localStorage,
  reducer: (state) => ({
    jobsList: {
      initialized: state.jobsList.initialized,
      visibleColumns: state.jobsList.visibleColumns,
      columnOrdering: state.jobsList.columnOrdering,
    },
    dayView: {
      showJobQueue: state.dayView.showJobQueue,
    },
    weekView: {
      selectedResourceTypeId: state.weekView.selectedResourceTypeId,
    },
    notifications: {
      token: state.notifications.token,
      binding: state.notifications.binding,
    },
    formSubmissionsFilters: {
      isAsideVisible: state.formSubmissionsFilters.isAsideVisible,
      inverseFormIds: state.formSubmissionsFilters.inverseFormIds,
      inverseFormActions: state.formSubmissionsFilters.inverseFormActions,
      tags: state.formSubmissionsFilters.tags,
      dateRange: {
        start: state.formSubmissionsFilters.dateRange.start,
        end: state.formSubmissionsFilters.dateRange.end,
      },
      subjectSearchIds: state.formSubmissionsFilters.subjectSearchIds,
      subjectSearchButtons: state.formSubmissionsFilters.subjectSearchButtons,
      inverseLocations: state.formSubmissionsFilters.inverseLocations,
      activeSavedFilterId: state.formSubmissionsFilters.activeSavedFilterId,
    },
    formSubmissions: {
      formFieldMemory: state.formSubmissions.formFieldMemory,
    },
  }),
  filter: (mutation) =>
    [
      `jobsList/${JOBS_LIST_INITIALIZED}`,
      `jobsList/${SET_VISIBLE_COLUMNS}`,
      `jobsList/${SET_COLUMN_ORDERING}`,
      `dayView/${SET_SHOW_JOBS_TABLE}`,
      `weekView/${SET_SELECTED_RESOURCE_TYPE_ID}`,
      `notifications/${SET_NOTIFICATIONS_TOKEN}`,
      `notifications/${SET_NOTIFICATIONS_BINDING}`,
      `formSubmissionsFilters/${SET_ASIDE_VISIBILITY}`,
      `formSubmissionsFilters/${SET_INVERSE_FORM_IDS}`,
      `formSubmissionsFilters/${SET_INVERSE_FORM_ACTIONS}`,
      `formSubmissionsFilters/${SET_TAGS}`,
      `formSubmissionsFilters/${SET_DATE_RANGE}`,
      `formSubmissionsFilters/${SET_SUBJECT_SEARCH_IDS}`,
      `formSubmissionsFilters/${SET_SUBJECT_SEARCH_BUTTONS}`,
      `formSubmissionsFilters/${SET_INVERSE_LOCATIONS}`,
      `formSubmissionsFilters/${RESET_SUBJECT_SEARCH_IDS}`,
      `formSubmissionsFilters/${RESET_INVERSE_LOCATIONS}`,
      `formSubmissionsFilters/${RESET_TAGS}`,
      `formSubmissionsFilters/${RESET_INVERSE_FORM_ACTIONS}`,
      `formSubmissionsFilters/${RESET_INVERSE_FORM_IDS}`,
      `formSubmissionsFilters/${SET_ACTIVE_SAVED_FILTER_ID}`,
      `formSubmissions/${SET_MEMORY_FIELDS}`,
    ].includes(mutation.type),
})

const plugins = [vuexLogRocketPlugin, vuexLocal.plugin]

const store = new Vuex.Store({
  state,
  actions,
  mutations,
  plugins,
  modules: {
    activities,
    attachments,
    businessUnits,
    companies,
    costCenters,
    dataErrors,
    dayView,
    divisions,
    featureFlags,
    firebase,
    formSubmissions,
    forms,
    formSubmissionsFilters,
    inventory,
    jobs,
    jobsList,
    notifications,
    organizationConfig,
    permissions,
    projects,
    resourceTypes,
    schemas,
    services,
    tasks,
    tickets,
    unitNumbers,
    users,
    weekView,
  },
  strict: debug,
})

store.watch(
  (_, getters) => getters['featureFlags/fieldViewDateWindow'],
  (enabled) => {
    dateWindowFlag.value = enabled
  },
  { immediate: true },
)
store.watch(
  (state) => state.organizationConfig.maxMapDateRangeDays,
  (maxDays) => {
    mapMaxDateRangeDays.value = maxDays
  },
  { immediate: true },
)

export default store
